import { useEffect, useMemo, useState } from "react";
import { BudgetRoundModel, DefaultBudgetRoundModel, GetCustomerDataResponse, GetCustomerSettingsResponse, UpdateDefaultBudgetRoundsRequest, YearAllotmentDataModel, YearDataModel, YearDefaultBudgetRoundModel } from "../../../../Generated/Commodity-Management-Api";
import { StoneXLoading, StoneXRow } from "../../../StoneX";
import { StoneXButton, StoneXModal, StoneXSelect } from "../../../StoneXMui";
import { CommodityManagementApi } from "../../../../Apis/Apis";
import { useLoadingState } from "../../../../Hooks";
import { enqueueSnackbar } from "notistack";
import { useApplicationSelector, RootState } from "../../../../Redux/ReduxStore";
import { toLookup } from "../../../../Helpers/ArrayHelper";
import { ChevronRight, ExpandMore } from "@mui/icons-material";
import { IconButton } from "@mui/material";

type GroupLevel = 'year' | 'commodityId' | 'locationId' | 'counterpartyId';

interface AllotmentTreeNode {
    level: GroupLevel;
    key: number;
    children: AllotmentTreeNode[];
    items: YearAllotmentDataModel[];
}

interface AllotmentPath {
    year?: number;
    commodityId?: number;
    locationId?: number;
    counterpartyId?: number;
}

interface UpdatePath {
    year: number;
    commodityId: number;
    locationId: number | null;
    counterpartyId: number | null;
}

interface UpdatedYearRoundSelection {
    path: UpdatePath;
    budgetRound: BudgetRoundModel | null;
}

function isValidUpdatedSelection(selection: unknown): selection is UpdatedYearRoundSelection {
    if (!selection) {
        return false;
    }

    const candidate = selection as Partial<UpdatedYearRoundSelection>;
    const path = candidate.path as Partial<UpdatePath> | undefined;

    return !!path && path.year != null && path.commodityId != null;
}

function getGroupLevels(budgetsByLocation: boolean, budgetsByCounterparty: boolean): GroupLevel[] {
    const levels: GroupLevel[] = ['year', 'commodityId'];

    if (budgetsByLocation) {
        levels.push('locationId');
    }

    if (budgetsByCounterparty) {
        levels.push('counterpartyId');
    }

    return levels;
}

function getLevelValue(item: YearAllotmentDataModel, level: GroupLevel): number | null | undefined {
    switch (level) {
        case 'year':
            return item.year;
        case 'commodityId':
            return item.commodityId;
        case 'locationId':
            return item.locationId;
        case 'counterpartyId':
            return item.counterpartyId;
    }
}

function buildAllotmentTree(items: YearAllotmentDataModel[], levels: GroupLevel[], depth: number = 0): AllotmentTreeNode[] {
    if (depth >= levels.length) {
        return [];
    }

    const level = levels[depth];
    const grouped = new Map<number, YearAllotmentDataModel[]>();

    items.forEach(item => {
        const value = getLevelValue(item, level);
        if (value == null) {
            return;
        }

        const existing = grouped.get(value) ?? [];
        existing.push(item);
        grouped.set(value, existing);
    });

    const sortedEntries = Array.from(grouped.entries()).sort((a, b) => {
        if (level === 'year') {
            return b[0] - a[0];
        }

        return a[0] - b[0];
    });

    return sortedEntries.map(([key, groupedItems]) => ({
        level,
        key,
        items: groupedItems,
        children: buildAllotmentTree(groupedItems, levels, depth + 1),
    }));
}

interface BudgetDefaultsPageModalProps {
    open: boolean;
    onClose: () => void;
    onSave: () => void;
}

export function BudgetDefaultsModal(props: BudgetDefaultsPageModalProps) {
    const { open, onClose, onSave } = props;

    const customerSettings = useApplicationSelector((state: RootState): GetCustomerSettingsResponse => state.commodityManagement.customerSettings);
    const customerData = useApplicationSelector((state: RootState): GetCustomerDataResponse => state.commodityManagement.customerData);
    
    const updateLoadingState = useLoadingState();
    const dataLoadingState = useLoadingState();

    const commodityIdToCommodity = useMemo(() => toLookup(customerData.commodities, x => x.commodityId), [customerData]);
    const locationIdToLocation = useMemo(() => toLookup(customerData.locations, x => x.locationId), [customerData]);
    const counterpartyIdToCounterparty = useMemo(() => toLookup(customerData.counterparties, x => x.counterpartyId), [customerData]);
    const budgetRoundIdToBudgetRound = useMemo(() => toLookup(customerData.budgetRounds, x => x.budgetRoundId), [customerData]);

    const [ updatedYearRounds, setUpdatedYearRounds ] = useState<Record<string, UpdatedYearRoundSelection>>({});
    const [ yearAllotmentProperties, setYearAllotmentProperties ] = useState<YearAllotmentDataModel[]>([]);
    const [ collapsedNodePaths, setCollapsedNodePaths ] = useState<Set<string>>(new Set());

    const [ defaultBudgetRounds, setDefaultBudgetRounds ] = useState<DefaultBudgetRoundModel[] | null>([]);
    const yearToDefaultBudgetRound = useMemo(() => {
        const mapping: {[year: number]: DefaultBudgetRoundModel | null} = {};
        defaultBudgetRounds?.forEach(x => {
            mapping[x.fiscalYear] = x;
        });
        return mapping;
    }, [defaultBudgetRounds]);

    const allotmentTree = useMemo(() => {
        const levels = getGroupLevels(customerSettings.budgetsByLocation ?? false, customerSettings.budgetsByCounterparty ?? false);
        return buildAllotmentTree(yearAllotmentProperties, levels);
    }, [yearAllotmentProperties, customerSettings.budgetsByCounterparty, customerSettings.budgetsByLocation]);

    function loadDefaultBudgetRounds() {
        CommodityManagementApi.listDefaultBudgetRounds(dataLoadingState.setLoadingState)
        .then(res => setDefaultBudgetRounds(res.data.rows ?? []))
        .catch(res => {});
    }

    function loadYearAllotmentProperties() {
        CommodityManagementApi.listBudgetYearAllotmentProperties(dataLoadingState.setLoadingState)
        .then(res => setYearAllotmentProperties(res.data.rows ?? []))
        .catch(res => {});
    }

    function toUpdatePath(path: AllotmentPath): UpdatePath | null {
        if (path.year == null || path.commodityId == null) {
            return null;
        }

        return {
            year: path.year,
            commodityId: path.commodityId,
            locationId: path.locationId ?? null,
            counterpartyId: path.counterpartyId ?? null,
        };
    }

    function getPathKey(path: UpdatePath) {
        return `${path.year}-${path.commodityId}-${path.locationId}-${path.counterpartyId}`;
    }

    function updateYearRound(path: UpdatePath, budgetRound: BudgetRoundModel | null) {
        const key = getPathKey(path);
        setUpdatedYearRounds(previous => ({
            ...previous,
            [key]: {
                path,
                budgetRound,
            },
        }));
    }

    function toggleNode(pathKey: string) {
        setCollapsedNodePaths(previous => {
            const next = new Set(previous);
            if (next.has(pathKey)) {
                next.delete(pathKey);
            } else {
                next.add(pathKey);
            }
            return next;
        });
    }

    function save() {
        const updates = Object.values(updatedYearRounds)
            .filter(isValidUpdatedSelection)
            .map(selection => ({
                year: selection.path.year,
                commodityId: selection.path.commodityId,
                locationId: selection.path.locationId,
                counterpartyId: selection.path.counterpartyId,
                budgetRoundId: selection.budgetRound?.budgetRoundId
            } as YearDefaultBudgetRoundModel));

        const request = {
            yearDefaultBudgetRounds: updates
        } as UpdateDefaultBudgetRoundsRequest;

        CommodityManagementApi.updateDefaultBudgetRounds(updateLoadingState.setLoadingState, request)
        .then(res => {
            enqueueSnackbar('Active budget rounds successfully updated', {variant: 'success'});
            onSave();
        })
        .catch(res => {
            enqueueSnackbar('Failed to set active budget rounds', {variant: 'error'});
        });
    }

    useEffect(() => {
        if (open) {
                    setUpdatedYearRounds({});
                    setCollapsedNodePaths(new Set());
          loadDefaultBudgetRounds();
          loadYearAllotmentProperties();
        }
    }, [open]);

    function getNodeLabel(node: AllotmentTreeNode): string {
        switch (node.level) {
            case 'year':
                return node.key.toString();
            case 'commodityId':
                return commodityIdToCommodity[node.key]?.commodityName ?? `Commodity ${node.key}`;
            case 'locationId':
                return locationIdToLocation[node.key]?.locationName ?? `Location ${node.key}`;
            case 'counterpartyId':
                return counterpartyIdToCounterparty[node.key]?.counterpartyName ?? `Counterparty ${node.key}`;
        }
    }

    function getPathWithNode(path: AllotmentPath, node: AllotmentTreeNode): AllotmentPath {
        switch (node.level) {
            case 'year':
                return { ...path, year: node.key };
            case 'commodityId':
                return { ...path, commodityId: node.key };
            case 'locationId':
                return { ...path, locationId: node.key };
            case 'counterpartyId':
                return { ...path, counterpartyId: node.key };
        }
    }

    function findAllotmentEntity(path: AllotmentPath): YearAllotmentDataModel | undefined {
        return yearAllotmentProperties.find(item =>
            item.year === path.year &&
            item.commodityId === path.commodityId &&
            (item.locationId ?? undefined) === path.locationId &&
            (item.counterpartyId ?? undefined) === path.counterpartyId,
        );
    }

    function getLeafBudgetRoundOptions(path: AllotmentPath): BudgetRoundModel[] {
        if (path.year == null || path.commodityId == null) {
            return [];
        }

        const allotmentEntity = findAllotmentEntity(path);

        if (!allotmentEntity) {
            return [];
        }

        return (allotmentEntity.budgetRoundIds ?? [])
            .map(budgetRoundId => budgetRoundIdToBudgetRound[budgetRoundId])
            .filter((budgetRound): budgetRound is BudgetRoundModel => !!budgetRound);
    }

    function renderNode(node: AllotmentTreeNode, pathKey: string, depth: number, parentPath: AllotmentPath): JSX.Element {
        const isLeaf = node.children.length === 0;
        const isCollapsible = (node.level === 'year' || node.level === 'commodityId') && !isLeaf;
        const isExpanded = !collapsedNodePaths.has(pathKey);
        const currentPath = getPathWithNode(parentPath, node);
        const leafBudgetRoundOptions = isLeaf ? getLeafBudgetRoundOptions(currentPath) : [];
        const selectedBudgetRound = (() => {
            const normalizedPath = toUpdatePath(currentPath);
            if (!normalizedPath) {
                return null;
            }

            const updatedSelection = updatedYearRounds[getPathKey(normalizedPath)]?.budgetRound;
            if (updatedSelection !== undefined) {
                return updatedSelection;
            }

            return leafBudgetRoundOptions.find(x => x.budgetRoundId === yearToDefaultBudgetRound[normalizedPath.year]?.budgetRoundId) ?? null;
        })();

        return (
            <div key={pathKey} style={{ marginLeft: depth * 20 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {isCollapsible ? (
                        <IconButton size="small" onClick={() => toggleNode(pathKey)}>
                            {isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                        </IconButton>
                    ) : (
                        <div style={{ width: 32 }} />
                    )}

                    {!isLeaf && <h4 style={{margin: 0, padding: 0}}>{getNodeLabel(node)}</h4>}
                    
                </div>

                {isLeaf && (
                    <div style={{ marginLeft: 20 }}>
                        <StoneXSelect
                            options={leafBudgetRoundOptions}
                            getId={x => x.budgetRoundId}
                            getOptionLabel={x => x.budgetRoundName}
                            value={selectedBudgetRound}
                            label={getNodeLabel(node)}
                            onChange={(budgetRound: BudgetRoundModel | null) => {
                                const normalizedPath = toUpdatePath(currentPath);
                                if (!normalizedPath) {
                                    return;
                                }

                                updateYearRound(normalizedPath, budgetRound);
                            }}
                        />
                    </div>
                )}

                {(isExpanded || !isCollapsible) && node.children.map(child => renderNode(child, `${pathKey}-${child.level}-${child.key}`, depth + 1, currentPath))}
            </div>
        );
    }

    return <StoneXModal header='Set Active Budget Rounds' open={open} onClose={onClose}>

        <StoneXLoading show={dataLoadingState.isLoading()}/>

        {dataLoadingState.isLoaded() && allotmentTree.map(node => renderNode(node, `${node.level}-${node.key}`, 0, {}))}

        <StoneXRow justify="end" marginDirection='top'>
            <StoneXButton variant="outlined" onClick={onClose}>Close</StoneXButton>
            <StoneXButton disabled={!dataLoadingState.isLoaded()} isWorking={updateLoadingState.isLoading()} onClick={save}>Save</StoneXButton>
        </StoneXRow>
    </StoneXModal>
}